import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { COMPANY_FACTS } from "@/content/company-facts";

/**
 * Publika företagsuppgifter (NAP) som redigeras i Admin → Företagsuppgifter.
 * Läses från vyn business_profile_public, som bara exponerar publika fält.
 * Cachas i minnet så rot-loadern inte gör en DB-träff per sidvisning.
 */
export interface PublicBusinessProfile {
  brand: string;
  legalName: string;
  phone: string;
  phoneHref: string;
  email: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  openingHours: string;
  socials: string[];
}

export const FALLBACK_PROFILE: PublicBusinessProfile = {
  brand: COMPANY_FACTS.brand,
  legalName: COMPANY_FACTS.legalName,
  phone: COMPANY_FACTS.phoneDisplay,
  phoneHref: COMPANY_FACTS.phone.replace(/\s+/g, ""),
  email: COMPANY_FACTS.email,
  streetAddress: COMPANY_FACTS.postalAddress.streetAddress,
  postalCode: COMPANY_FACTS.postalAddress.postalCode,
  city: COMPANY_FACTS.postalAddress.addressLocality,
  region: COMPANY_FACTS.postalAddress.addressRegion,
  country: COMPANY_FACTS.postalAddress.addressCountry,
  openingHours: COMPANY_FACTS.openingHours,
  socials: [],
};

const TTL_MS = 5 * 60 * 1000;
let cache: { at: number; value: PublicBusinessProfile } | null = null;

function displayPhone(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  // +46 8 480 047 06 → 08 480 047 06 för svensk visning.
  return t.startsWith("+46") ? `0${t.slice(3).trim()}` : t;
}

export const getPublicBusinessProfile = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicBusinessProfile> => {
    if (cache && Date.now() - cache.at < TTL_MS) return cache.value;

    const url = process.env["SUPABASE_URL"];
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"];
    if (!url || !key) return FALLBACK_PROFILE;

    try {
      const supabase = createClient<Database>(url, key, {
        auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
      });
      const { data, error } = await supabase
        .from("business_profile_public")
        .select(
          "brand, legal_name, phone, email, street_address, postal_code, city, region, country, opening_hours, socials",
        )
        .eq("id", "default")
        .maybeSingle();
      if (error || !data) return FALLBACK_PROFILE;

      const socials = Object.values((data.socials ?? {}) as Record<string, string>)
        .map((s) => (s ?? "").trim())
        .filter((s) => s.startsWith("http"));

      const value: PublicBusinessProfile = {
        brand: data.brand?.trim() || FALLBACK_PROFILE.brand,
        legalName: data.legal_name?.trim() || FALLBACK_PROFILE.legalName,
        phone: displayPhone(data.phone ?? "") || FALLBACK_PROFILE.phone,
        phoneHref: (data.phone?.trim() || COMPANY_FACTS.phone).replace(/\s+/g, ""),
        email: data.email?.trim() || FALLBACK_PROFILE.email,
        streetAddress: data.street_address?.trim() ?? "",
        postalCode: data.postal_code?.trim() ?? "",
        city: data.city?.trim() || FALLBACK_PROFILE.city,
        region: data.region?.trim() ?? "",
        country: data.country?.trim() || "SE",
        openingHours: data.opening_hours?.trim() ?? "",
        socials,
      };
      cache = { at: Date.now(), value };
      return value;
    } catch {
      return FALLBACK_PROFILE;
    }
  },
);
